import React from 'react';
import { useTranslation } from 'react-i18next';
import { MenuItem, Menu, Typography, IconButton, Tooltip } from '@mui/material';
import { Language, ExpandMore } from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '@/config';
import { supportedLanguages } from '@/translations/config';

const LanguagePicker = () => {
  const { i18n, t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isUserAuthenticated = () => {
    try {
      const appState = JSON.parse(localStorage.getItem('appState') || '{}');
      return appState.isLoggedIn && appState.user;
    } catch {
      return false;
    }
  };

  const updateLanguagePreferenceOnServer = async (languageCode) => {
    try {
      await axios.put(`${API_BASE_URL}/user/language-preference`, {
        language_preference: languageCode,
      });
    } catch (error) {
      console.error('Failed to update language preference:', error);
      toast.error(t('errors.languageUpdateFailed'));
    }
  };

  const handleLanguageChange = async (languageCode) => {
    handleClose();

    i18n.changeLanguage(languageCode);

    if (isUserAuthenticated()) {
      await updateLanguagePreferenceOnServer(languageCode);
    }
  };

  const currentLanguage = supportedLanguages.find(
    (lang) => lang.value === i18n.language,
  );

  return (
    <>
      <Tooltip title='Language settings'>
        <IconButton
          onClick={handleClick}
          size='small'
          aria-controls={open ? 'language-menu' : undefined}
          aria-haspopup='true'
          aria-expanded={open ? 'true' : undefined}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            paddingX: 0.5,
            paddingY: 0.5,
            borderRadius: 2,
          }}>
          <Language sx={{ fontSize: 18 }} />
          <Typography
            variant='body2'
            sx={{
              color: 'text.primary',
              display: { xs: 'none', sm: 'block' },
            }}>
            {currentLanguage?.label || 'English'}
          </Typography>
          <ExpandMore fontSize='small' sx={{ color: 'text.secondary' }} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        id='language-menu'
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
        {supportedLanguages.map((lang) => (
          <MenuItem
            key={lang.value}
            onClick={() => handleLanguageChange(lang.value)}
            sx={{
              justifyContent: 'space-between',
              ...(lang.value === i18n.language && {
                backgroundColor: 'action.selected',
              }),
            }}>
            <Typography variant='body2'>{lang.label}</Typography>
            <Typography variant='body2' color='text.secondary' sx={{ ml: 3 }}>
              {lang.code}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguagePicker;
